import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FeedClippingCount, ReportConfigurationModel, ReportConfigurationType, ReportDefinitionSettings, ReportDefinition } from 'store/models/reportConfiguration';

export type State = {
	originalReportDefinition: ReportDefinition;
	reportError: string;
	saveReportModalOpen: boolean;
	closeWithoutSavingModalOpen: boolean
	formDisabled: boolean;
	configuration: ReportConfigurationModel;
};

export const DEFAULT_DEFINITION: ReportDefinition = {
	search: {
		query: '',
		date_type: 'publication_date'
	},
	settings: {
		type: 'coverage',
		period: 'monthly',
		language: 'en',
		currency: 'EUR',
		minDate: null,
		maxDate: null,
		groupedByCountry: false,
		feedClippings: [],
		comparePeriodsMode: 'custom'
	}
};

export const INITIAL_STATE: State = {
	originalReportDefinition: DEFAULT_DEFINITION,
	reportError: '',
	saveReportModalOpen: false,
	closeWithoutSavingModalOpen: false,
	formDisabled: false,
	configuration: {
		id: null,
		name: null,
		type: '',
		definition: DEFAULT_DEFINITION
	}
};

const slice = createSlice({
	name: 'report',
	initialState: INITIAL_STATE,
	reducers: {
		resetReport: () => INITIAL_STATE,
		setReportById: (state, action: PayloadAction<{ id: number, navigate?: boolean }>) => {
			state.configuration.id = action.payload.id;
		},
		setReportType: (state, action: PayloadAction<{type: ReportConfigurationType}>) => {
			state.configuration.type = action.payload.type;
		},
		setOriginalDefinition: (state, action: PayloadAction<ReportDefinition>) => {
			state.originalReportDefinition = action.payload;
		},
		setReportName: (state, action: PayloadAction<string>) => {
			state.configuration.name = action.payload;
		},
		setSaveReportModalOpen: (state, action: PayloadAction<boolean>) => {
			state.saveReportModalOpen = action.payload;
		},
		setCloseWithoutSavingModalOpen: (state, action: PayloadAction<boolean>) => {
			state.closeWithoutSavingModalOpen = action.payload;
		},
		setReportSettings: (state, action: PayloadAction<Partial<ReportDefinitionSettings>>) => {
			state.configuration.definition.settings = { ...state.configuration.definition.settings, ...action.payload };
		},
		setReportClipping: (state, action: PayloadAction<{feedId: string, count: FeedClippingCount}>) => {
			const newClippings = [...state.configuration.definition.settings.feedClippings];
			for (const clipping of newClippings) {
				if (clipping.id === action.payload.feedId) {
					clipping.count = action.payload.count;
					state.configuration.definition.settings.feedClippings = newClippings;
					return;
				}
			}
			state.configuration.definition.settings.feedClippings = [...newClippings, { id: action.payload.feedId, count: action.payload.count }];
		},
		removeReportClipping: (state, action: PayloadAction<{feedId: string}>) => {
			state.configuration.definition.settings.feedClippings = state.configuration.definition.settings.feedClippings.filter(clipping => clipping.id !== action.payload.feedId);
		},
		clearReportClippings: state => {
			state.configuration.definition.settings.feedClippings = [];
		},
		setReportError: (state, action: PayloadAction<string>) => {
			state.reportError = action.payload;
		},
		setReportConfiguration: (state, action: PayloadAction<ReportConfigurationModel>) => {
			state.configuration = action.payload;
		},
		setReportFormDisabled: (state, action: PayloadAction<boolean>) => {
			state.formDisabled = action.payload;
		}
	}
});

export const {
	resetReport,
	setReportType,
	setOriginalDefinition,
	setReportById,
	setReportName,
	setSaveReportModalOpen,
	setCloseWithoutSavingModalOpen,
	setReportSettings,
	setReportError,
	setReportClipping,
	removeReportClipping,
	clearReportClippings,
	setReportConfiguration,
	setReportFormDisabled
} = slice.actions;

export default slice.reducer;
